import './App.css';
import { AuthProvider } from '@/providers/AuthProvider';
import { ThemeProvider } from '@/providers/ThemeProvider';
import { Toaster } from '@/components/ui/sonner';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { PublicRoute, PrivateRoute } from '@/components/auth/ProtectedRoute';
import { EmailVerificationGuard } from '@/components/auth/EmailVerificationGuard';
import Landing from '@/pages/Landing';
import LoginPage from '@/pages/auth/Login';
import RegisterPage from '@/pages/auth/Register';
import ForgotPasswordPage from '@/pages/auth/ForgotPassword';
import ResetPasswordPage from '@/pages/auth/ResetPassword';
import EmailVerificationPage from '@/pages/auth/EmailVerification';
import AppLayout from '@/pages/app/AppLayout';
import { useEffect, lazy, Suspense } from 'react';
import TermsPage from '@/pages/Terms';
import PrivacyPage from '@/pages/Privacy';
import ContactPage from '@/pages/Contact';

// Lazy load candidate session components (heavy, separate from main app)
const StartSessionPage = lazy(() => import('@/pages/candidate/StartSession'));
const CandidateSessionPlaceholder = lazy(() => import('@/pages/candidate/Session'));
const SessionComplete = lazy(() => import('@/pages/candidate/SessionComplete'));

// Loading component for candidate session routes
const CandidateSessionLoading = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="text-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
      <p className="text-muted-foreground">Loading assessment session...</p>
    </div>
  </div>
);

// Smoothly scroll to hash targets after route / hash changes
function ScrollToHashHandler() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      const el = document.getElementById(id);
      if (el) {
        // account for sticky header (approx 72px)
        const y = el.getBoundingClientRect().top + window.scrollY - 72;
        window.scrollTo({ top: y < 0 ? 0 : y, behavior: 'smooth' });
      }
    }
  }, [location.pathname, location.hash]);
  return null;
}

function App() {
  return (
    <ThemeProvider defaultTheme="system" enableSystem>
      <BrowserRouter>
        <AuthProvider>
          <ScrollToHashHandler />
          <Routes>
            {/* Public routes - only accessible when NOT authenticated */}
            <Route path="/" element={
              <PublicRoute>
                <Landing />
              </PublicRoute>
            } />
            <Route path="/login" element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            } />
            <Route path="/register" element={
              <PublicRoute>
                <RegisterPage />
              </PublicRoute>
            } />
            <Route path="/forgot-password" element={
              <PublicRoute>
                <ForgotPasswordPage />
              </PublicRoute>
            } />
            <Route path="/reset-password" element={
              <PublicRoute>
                <ResetPasswordPage />
              </PublicRoute>
            } />

            {/* Legal and contact pages - accessible to anyone */}
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/contact" element={<ContactPage />} />

            {/* Email verification route - accessible to anyone */}
            <Route path="/verify-email" element={<EmailVerificationPage />} />

            <Route path="/candidate/start" element={
              <Suspense fallback={<CandidateSessionLoading />}>
                <StartSessionPage />
              </Suspense>
            } />
            <Route path="/candidate/session/:sessionId" element={
              <Suspense fallback={<CandidateSessionLoading />}>
                <CandidateSessionPlaceholder />
              </Suspense>
            } />
            <Route path="/candidate/session-complete" element={
              <Suspense fallback={<CandidateSessionLoading />}>
                <SessionComplete />
              </Suspense>
            } />

            {/* Private routes - only accessible when authenticated and email verified */}
            <Route path="/app/*" element={
              <PrivateRoute>
                <EmailVerificationGuard>
                  <AppLayout />
                </EmailVerificationGuard>
              </PrivateRoute>
            } />

            {/* Catch all route - redirect based on auth status */}
            <Route path="*" element={
              <PublicRoute redirectTo="/app">
                <Landing />
              </PublicRoute>
            } />
          </Routes>
          <Toaster position='bottom-center' richColors theme="light" />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
